import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Separator } from '@/shared/ui/separator';
import {PageLoader} from '@/shared/ui/page-loader';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { authApi } from '@/features/auth/services/auth.api';
import { isEntraAuthEnabled } from '@/config/env';
import { handleMsalRedirect, signInWithMicrosoft, getMicrosoftAccessToken } from '@/config/msalConfig';

// Helper to decode JWT payload safely (returns null if not a JWT)
const decodeJwtPayload = (token: string | null) => {
  try {
    if (!token) return null;
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// Opaque auth tokens are base64url / JWT-shaped strings. Anything containing
// other characters is treated as untrusted and is NOT persisted, to guard
// against browser-storage poisoning via a crafted SSO callback URL.
const SAFE_TOKEN_PATTERN = /^[A-Za-z0-9._~+/=-]+$/;
const isSafeTokenValue = (value: string | null | undefined): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= 8192 &&
  SAFE_TOKEN_PATTERN.test(value);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/;

// Parse SSO callback params from the URL search string or hash fragment.
const parseSsoCallbackParams = (): URLSearchParams => {
  const hash = globalThis.location.hash.startsWith('#')
    ? globalThis.location.hash.substring(1)
    : globalThis.location.hash;
  const raw = globalThis.location.search || (hash ? `?${hash}` : '');
  return new URLSearchParams(raw);
};

// True when the URL carries any SSO token param.
const hasSsoTokenParams = (params: URLSearchParams): boolean =>
  params.has('access_token') ||
  params.has('refresh_token') ||
  params.has('id_token') ||
  params.has('token');

// Persist SSO tokens (and a derived expiry) to localStorage.
const persistSsoTokens = (
  accessToken: string,
  refreshToken: string | null,
  idToken: string | null,
  expiresIn: string | null,
): void => {
  if (isSafeTokenValue(accessToken)) {
    localStorage.setItem('access_token', accessToken);
  }
  if (isSafeTokenValue(refreshToken)) {
    localStorage.setItem('refresh_token', refreshToken);
  }
  if (isSafeTokenValue(idToken)) {
    localStorage.setItem('id_token', idToken);
  }
  if (expiresIn) {
    localStorage.setItem(
      'token_expiry',
      (Date.now() + Number(expiresIn) * 1000).toString(),
    );
  } else {
    // default to 1h if IdP doesn't provide expires_in
    localStorage.setItem('token_expiry', String(Date.now() + 3600 * 1000));
  }
};

// Best-effort exchange of an IdP id_token for a Supabase session.
const exchangeIdTokenForSession = async (idToken: string): Promise<void> => {
  try {
    const {error: idpError} = await supabase.auth.signInWithIdToken({
      provider: 'azure',
      token: idToken,
    });
    if (idpError) {
      console.warn(
        '[SSO] signInWithIdToken failed (will continue with fallback):',
        idpError.message,
      );
    }
  } catch (e) {
    console.warn('[SSO] signInWithIdToken unexpected error:', e);
  }
};

type SsoUserDetails = {empId: any; name: string; email: string};

// Extract employee id / name / email from JWT claims, or null if incomplete.
const extractSsoUserDetails = (payload: any): SsoUserDetails | null => {
  const empId =
    payload.login_uid ||
    payload.empId ||
    payload.employeeId ||
    payload.emp_id ||
    payload.sub;
  const firstName = payload.first_name || payload.given_name;
  const lastName = payload.last_name || payload.family_name;
  const email =
    payload.email ||
    payload.preferred_username ||
    payload.upn ||
    payload.unique_name ||
    payload.user_email;
  const name =
    [firstName, lastName].filter(Boolean).join(' ') ||
    payload.name ||
    email ||
    'Unknown';
  // Require a well-formed email before we persist any centific_user record,
  // so untrusted JWT claims can't poison browser storage.
  if (empId && name && email && EMAIL_PATTERN.test(String(email))) {
    return {empId, name, email};
  }
  return null;
};

// Build the centific_user payload stored for AuthContext to pick up.
const buildCentificUserData = (
  details: SsoUserDetails,
  accessToken: string,
  refreshToken: string | null,
  expiresIn: string | null,
) => ({
  id: details.empId,
  name: details.name,
  email: details.email,
  accessToken,
  refreshToken,
  expiresAt: expiresIn
    ? Date.now() + Number(expiresIn) * 1000
    : Date.now() + 3600 * 1000,
});

// Idempotently ensure the user exists in Supabase auth (single retry on rate limit).
const provisionSupabaseUserOnce = async (
  email: string,
  name: string,
  empId: any,
): Promise<boolean> => {
  const provisionKey = `supabase_user_provisioned:${email.toLowerCase()}`;
  let createdSupabaseUser = false;
  const alreadyProvisioned = localStorage.getItem(provisionKey) === 'true';
  try {
    if (!alreadyProvisioned) {
      // Cryptographically strong throwaway password: this credential is never
      // used to log in (auth is via SSO), but a predictable value would let an
      // attacker who knows the email sign in directly and bypass SSO.
      const passwordBytes = new Uint8Array(24);
      crypto.getRandomValues(passwordBytes);
      const randomPassword = `centific-${Array.from(passwordBytes, (b) =>
        b.toString(16).padStart(2, '0'),
      ).join('')}`;
      const attemptSignUp = async () =>
        supabase.auth.signUp({
          email,
          password: randomPassword,
          options: {data: {name, employee_id: empId, provider: 'centific'}},
        });
      let {error: signUpError} = await attemptSignUp();
      if (
        signUpError &&
        String(signUpError.message || '').includes('Too Many Requests')
      ) {
        await new Promise((r) => setTimeout(r, 800));
        ({error: signUpError} = await attemptSignUp());
      }
      if (!signUpError) {
        createdSupabaseUser = true;
        localStorage.setItem(provisionKey, 'true');
      } else if (/already\s+registered/i.test(signUpError.message)) {
        localStorage.setItem(provisionKey, 'true');
      } else {
        console.warn('[SSO] Supabase signUp error (non-fatal):', signUpError.message);
      }
    }
  } catch (err) {
    console.warn('[SSO] Unexpected error creating Supabase user (non-fatal):', err);
  }
  return createdSupabaseUser;
};

// Decide the post-SSO landing route.
const resolveSsoLandingPath = (
  createdSupabaseUser: boolean | undefined,
  payload: any,
  fallbackEmail: string,
): string => {
  if (typeof createdSupabaseUser !== 'undefined' && createdSupabaseUser) {
    return '/pending-approval';
  }
  const emailFromToken =
    (payload &&
      (payload.email ||
        payload.preferred_username ||
        payload.upn ||
        payload.unique_name ||
        payload.user_email)) ||
    undefined;
  const email = emailFromToken || fallbackEmail || '';
  return /@centific\.com$/i.test(email) ? '/' : '/participant';
};

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showInvitationSuccess, setShowInvitationSuccess] = useState(false);
  const [isProcessingSso, setIsProcessingSso] = useState(false);

  const { signIn, signUp, user, refreshUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Show success text above login when redirected here after accepting a team invitation (does not sign the user in)
  useEffect(() => {
    if (searchParams.get('invitation') === 'accepted') {
      setShowInvitationSuccess(true);
      const next = new URLSearchParams(searchParams);
      next.delete('invitation');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // SSO Configuration from environment variables
  const SSO_BASE_URL = import.meta.env.VITE_CENTIFIC_SSO_URL;
  const CLIENT_ID = import.meta.env.VITE_CENTIFIC_CLIENT_ID;
  
  // Check if we're in development mode
  const isDevelopment = globalThis.location.hostname === 'localhost' || globalThis.location.hostname === '127.0.0.1';
  
  // Use local URL for development, Azure URL for production
  // Can be overridden via VITE_SSO_REDIRECT_URL env variable
  const REDIRECT_URL = import.meta.env.VITE_SSO_REDIRECT_URL || (
    isDevelopment 
      ? `${globalThis.location.origin}/` 
      : (import.meta.env.VITE_PRODUCTION_REDIRECT_URL || 'https://one-data-platform-fe.azurewebsites.net/')
  );

  // Handle SSO callback: support tokens in either query or hash (some IdPs return fragments in local dev)
  useEffect(() => {
    // Handle the SSO access-token flow: persist tokens, provision user, navigate.
    const processSsoAccessToken = async (
      accessToken: string,
      refreshToken: string | null,
      expiresIn: string | null,
      idToken: string | null,
    ) => {
      try {
        setIsLoading(true);
        // Try to set Supabase session if possible
        const {error} = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || '',
        });
        if (error) {
          console.warn(
            'Supabase setSession failed (expected for non-Supabase tokens):',
            error.message,
          );
        }

        // Fallback persistence for SSO-only tokens
        persistSsoTokens(accessToken, refreshToken, idToken, expiresIn);

        // If we have an IdP id_token, try to exchange it for a Supabase session
        if (idToken) await exchangeIdTokenForSession(idToken);

        // Extract employee id and name from token claims
        let createdSupabaseUser: boolean | undefined = undefined;
        const payload = decodeJwtPayload(idToken || accessToken);
        if (payload) {
          const details = extractSsoUserDetails(payload);
          if (details) {
            // Store Centific user details for the AuthContext to pick up
            const centificUserData = buildCentificUserData(
              details,
              accessToken,
              refreshToken,
              expiresIn,
            );
            localStorage.setItem('centific_user', JSON.stringify(centificUserData));

            // Best-effort ensure user exists in Supabase auth (idempotent + single retry)
            createdSupabaseUser = await provisionSupabaseUserOnce(
              details.email,
              details.name,
              details.empId,
            );

            // Notify other components and refresh the user data in the context
            globalThis.dispatchEvent(
              new StorageEvent('storage', {
                key: 'centific_user',
                newValue: JSON.stringify(centificUserData),
              }),
            );
            refreshUser();
          } else {
            console.warn('[SSO] Missing required user details from JWT');
          }
        } else {
          console.warn('[SSO] Unable to decode JWT to extract employee details');
        }

        navigate(resolveSsoLandingPath(createdSupabaseUser, payload, user?.email || ''));
      } catch (e) {
        console.error('Error handling SSO callback:', e);
        toast({
          title: 'SSO Error',
          description: 'Failed to process SSO login.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
        setIsProcessingSso(false);
        // Clean both search and hash to avoid reprocessing
        globalThis.history.replaceState({}, document.title, globalThis.location.pathname);
      }
    };

    const handleSSOCallback = async () => {
      const params = parseSsoCallbackParams();
      if (hasSsoTokenParams(params)) {
        setIsProcessingSso(true);
      }

      // SAML redirect support: backend may redirect with `token`
      const samlToken = params.get('token');
      if (samlToken && !isSafeTokenValue(samlToken)) {
        console.warn('[Auth] Ignoring malformed SAML token from callback URL');
      } else if (samlToken) {
        try {
          localStorage.setItem('auth_token', samlToken);
          // Clean URL to avoid leaking token in history
          globalThis.history.replaceState({}, document.title, globalThis.location.pathname);
          navigate('/');
          return;
        } catch (e) {
          console.warn('[Auth] Failed storing SAML token:', e);
          setIsProcessingSso(false);
        }
      }

      const accessToken = params.get('access_token');
      if (!accessToken) {
        setIsProcessingSso(false);
        return;
      }

      await processSsoAccessToken(
        accessToken,
        params.get('refresh_token'),
        params.get('expires_in'),
        params.get('id_token'),
      );
    };

    // Only attempt if not already authenticated
    if (!user) {
      void handleSSOCallback();
    }
  }, [navigate, toast, user]);

  // Exchanges a verified Microsoft access token for this app's own login
  // ticket, the same way the existing SSO/local flows do (same storage key,
  // same post-login navigation).
  const completeEntraLogin = async (accessToken: string) => {
    try {
      const response = await authApi.entraCallback({ accessToken });
      const token = response?.data?.token;
      if (!isSafeTokenValue(token)) {
        throw new Error('No valid token returned from server');
      }
      localStorage.setItem('auth_token', token);
      await refreshUser();
      navigate('/');
    } catch (error) {
      console.error('[Entra] Sign-in exchange failed:', error);
      toast({
        title: 'Sign-in Failed',
        description: 'Failed to complete Microsoft sign-in. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsProcessingSso(false);
    }
  };

  // Picks up the browser landing back from Microsoft's login page after a
  // full-page redirect (the ssoSilent-failed fallback in handleEntraSignIn).
  useEffect(() => {
    if (!isEntraAuthEnabled() || user) return;

    const resumeEntraRedirect = async () => {
      try {
        const result = await handleMsalRedirect();
        if (!result?.account) return;
        setIsProcessingSso(true);
        const accessToken = await getMicrosoftAccessToken(result.account);
        if (accessToken) {
          await completeEntraLogin(accessToken);
        } else {
          setIsProcessingSso(false);
        }
      } catch (error) {
        console.error('[Entra] Redirect handling failed:', error);
        setIsProcessingSso(false);
      }
    };

    void resumeEntraRedirect();
  }, []);

  if (isProcessingSso) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <PageLoader message="Signing in..." size="xl" />
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (error) {
      const msg = String(error.message || '').toLowerCase();
      const needsVerification =
        msg.includes('not confirmed') ||
        msg.includes('verify your email') ||
        msg.includes('email not confirmed') ||
        msg.includes('verification') ||
        msg.includes('confirmation');
      if (needsVerification) {
        toast({
          title: "Awaiting Verification",
          description: "Your account is pending verification. You'll be able to sign in once approved.",
        });
        navigate('/pending-approval');
        setIsLoading(false);
        return;
      } else {
        toast({
          title: "Sign In Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Welcome back!",
        description: "You have been successfully signed in.",
      });
    }
    
    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const { error } = await signUp(email, password, displayName);
    
    if (error) {
      toast({
        title: "Sign Up Failed",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Account Created!",
        description: "Please check your email to verify your account.",
      });
    }
    
    setIsLoading(false);
  };

  const handleEntraSignIn = async () => {
    try {
      setIsLoading(true);
      // Always a full-page redirect to Microsoft - the browser navigates
      // away here; resumeEntraRedirect (below) picks the flow back up when
      // Microsoft sends the browser back.
      await signInWithMicrosoft();
    } catch (error) {
      console.error('[Entra] Sign-in error:', error);
      toast({
        title: 'Sign-in Failed',
        description: 'An error occurred during Microsoft sign-in. Please try again.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  const handleCentificSignIn = async () => {
    try {
      setIsLoading(true);

      const loginUrl = `${SSO_BASE_URL}?clientId=${CLIENT_ID}&redirectUrl=${encodeURIComponent(REDIRECT_URL)}`;
      globalThis.location.href = loginUrl;

    } catch (error) {
      console.error('SSO Login Error:', error);
      toast({
        title: "SSO Login Failed",
        description: "An error occurred during SSO login. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  // Single SSO entry point: which flow actually runs is decided by the
  // ENTRA_AUTH_ENABLED flag, not by which button was clicked - the button
  // itself is one unchanged element either way.
  const handleSsoSignIn = () =>
    isEntraAuthEnabled() ? handleEntraSignIn() : handleCentificSignIn();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      {showInvitationSuccess && (
        <p className="mb-4 text-center text-lg text-green-600 font-medium max-w-md">
          Invitation accepted. You&apos;ve been added to the project. Sign in below to get started.
        </p>
      )}
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Welcome</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account or create a new one
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin" className="space-y-4">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-black hover:bg-black/90 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Display Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Enter your display name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="Create a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-black hover:bg-black/90 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating Account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
            
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                onClick={handleSsoSignIn}
                disabled={isLoading}
                className="w-full max-w-sm py-6"
                aria-label={isEntraAuthEnabled() ? 'Sign in with Microsoft' : 'Sign in with Centific SSO'}
              >
                <div className="flex items-center justify-center gap-4">
                  <img
                    src="/logo.svg"
                    alt="SSO Login"
                    className="h-6 w-6"
                  />
                  {/* Google */}
                  <svg className="h-6 w-6" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
                    <title>Google</title>
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.826 32.91 29.276 36 24 36 16.82 36 11 30.18 11 23S16.82 10 24 10c3.59 0 6.84 1.355 9.343 3.557l5.657-5.657C35.227 3.488 29.918 1 24 1 11.85 1 2 10.85 2 23s9.85 22 22 22c12.15 0 22-9.85 22-22 0-1.474-.152-2.914-.389-4.417z"/>
                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.814C14.5 16.42 18.912 13 24 13c3.59 0 6.84 1.355 9.343 3.557l5.657-5.657C35.227 3.488 29.918 1 24 1 16.318 1 9.597 4.942 6.306 10.691c-.79 1.367-1.382 2.844-1.77 4.379z"/>
                    <path fill="#4CAF50" d="M24 45c5.16 0 9.797-1.977 13.294-5.188l-6.14-5.2C29.742 35.57 27.017 36.5 24 36.5c-5.248 0-9.782-3.567-11.392-8.41l-6.52 5.02C8.315 40.68 15.59 45 24 45z"/>
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-1.214 3.537-4.13 6.266-7.849 7.379l6.14 5.2C36.606 37.873 40 31.5 40 24c0-1.474-.152-2.914-.389-4.417z"/>
                  </svg>
                  {/* Microsoft */}
                  <svg className="h-6 w-6" viewBox="0 0 23 23" aria-hidden="true" focusable="false">
                    <title>Microsoft</title>
                    <path d="M0 0h11v11H0V0z" fill="#f25022"/>
                    <path d="M12 0h11v11H12V0z" fill="#00a4ef"/>
                    <path d="M0 12h11v11H0V12z" fill="#ffb900"/>
                    <path d="M12 12h11v11H12V12z" fill="#7fba00"/>
                  </svg>
                </div>
              </Button>
            </div>
          </div>
        </CardContent>

      </Card>
    </div>
  );
};

export default Auth;